import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import type { StreamStatus, Unsubscribe } from '../types';

export interface AdapterOHLCVRequest {
  /** Canonical `BASE/QUOTE`. */
  pair: string;
  timeframe: Timeframe;
  /** Unix ms, inclusive. */
  from: number;
  /** Unix ms, inclusive. */
  to: number;
}

/**
 * One exchange's public market data. Adapters translate between the venue's
 * symbols/intervals and HopCharts' canonical `BASE/QUOTE` pairs and Timeframes.
 *
 * Rules shared by every adapter:
 * - `getOHLCV` resolves oldest-first, one candle per timestamp (Unix ms, bucket
 *   open), covering `[from, to]` as far as the venue allows, paging under the
 *   venue's per-call limit. Timeframes the venue lacks are built from a finer
 *   native one with `aggregateToTimeframe` (UTC buckets, weeks on Monday,
 *   months on the 1st). An empty window resolves `[]`.
 * - Bucket alignment must be UTC (some venues default to UTC+8 — use their
 *   UTC variants).
 * - Pairs use the venue's display asset codes normalised to the common ones
 *   (Kraken XBT → BTC, XDG → DOGE).
 * - Errors throw `ProviderError` with the HTTP status when there is one.
 * - Streams use `ReconnectingSocket` and never throw from callbacks.
 */
/**
 * Up to this many named markets, an adapter that can fetch tickers one market at a time does so instead of
 * listing the whole exchange (Bybit's full spot list is ~430 KB uncompressed, Kraken's ~190 KB, OKX's ~100 KB).
 */
export const FEW_TICKERS = 10;

export interface ExchangeAdapter {
  id: string;
  name: string;
  getPairs(): Promise<TradingPair[]>;
  getOHLCV(request: AdapterOHLCVRequest): Promise<OHLCV[]>;
  /** Every market's 24h ticker; given `pairs`, an adapter may fetch only those (others list all and the caller filters). */
  getTickers(pairs?: string[]): Promise<Ticker24h[]>;
  subscribeOHLCV?(
    pair: string,
    timeframe: Timeframe,
    onCandle: (candle: OHLCV) => void,
    onStatus?: (status: StreamStatus) => void,
  ): Unsubscribe;
  subscribeTickers?(pairs: string[], onTicker: (ticker: Ticker24h) => void): Unsubscribe;
}

export interface AdapterOptions {
  /** Custom fetch (tests, server-side use). */
  fetch?: typeof fetch;
  /** Custom WebSocket constructor (tests, Node). Defaults to globalThis.WebSocket. */
  WebSocket?: typeof WebSocket;
}
